import { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  PanelRightClose,
  PanelRightOpen,
  Check,
  Trash2,
  FileCheck,
  Lock,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { useNotes } from '../../hooks/cabin/useNotes';
import { isAssessmentUnlocked } from '../../utils/assessment';
import { getStudentCount } from '../../lib/studentCount';

// Kabin Memurluğu kursu kendi sınav/eligibility akışına sahip (bkz. CabinDashboard,
// useCabinEnrolled, CabinEnrollmentGuard — hepsi aynı sabiti kullanır). Ders akışı
// içindeki genel "Değerlendirme" öğesi bu kursta RENDER EDİLMEZ.
const CABIN_COURSE_ID = 'course_cabin_main_v1';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(minutes, lang) {
  if (!minutes || minutes <= 0) return null;
  return lang === 'tr' ? `${minutes} dak` : `${minutes} min`;
}

// ─── Üst bar ─────────────────────────────────────────────────────────────────

function CabinTopBar({
  lesson,
  course,
  getField,
  lang: _lang,
  t,
  completedCount,
  totalCount,
  sidebarOpen,
  onToggleSidebar,
  courseId,
  navigate,
}) {
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const progressLabel = t('cabin.lesson.progress_label')
    .replace('{done}', completedCount)
    .replace('{total}', totalCount);

  return (
    <header
      className="bg-[var(--cabin-bar-bg)] text-white px-4 py-2 flex items-center gap-3 sticky top-0 z-30 shrink-0 border-b border-[var(--cabin-divider)]"
      data-testid="cabin-udemy-topbar"
    >
      {/* Geri */}
      <button
        onClick={() => navigate('/courses/' + courseId)}
        className="text-[#7BAABF] hover:text-white transition shrink-0"
        aria-label={t('cabin.lesson.back_to_course')}
        data-testid="cabin-back-btn"
      >
        <ChevronLeft size={22} strokeWidth={2.5} />
      </button>

      {/* Ders başlığı */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-[#7BAABF] font-semibold truncate">
          {getField(course, 'title') || ''}
        </p>
        <p className="text-sm font-bold truncate" data-testid="cabin-lesson-title">
          {getField(lesson, 'title')}
        </p>
      </div>

      {/* İlerleme */}
      <div
        className="hidden sm:flex flex-col items-end gap-1 shrink-0 min-w-[120px]"
        data-testid="cabin-progress-area"
      >
        <span className="text-[11px] font-semibold text-[#7BAABF]">{progressLabel}</span>
        <div className="w-28 h-1.5 bg-[#1E4060] rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--brand-primary)] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Panel toggle */}
      <button
        onClick={onToggleSidebar}
        className="text-[#7BAABF] hover:text-white transition shrink-0 ml-1"
        aria-label={sidebarOpen
          ? t('cabin.lesson.sidebar_toggle_close')
          : t('cabin.lesson.sidebar_toggle_open')}
        data-testid="cabin-sidebar-toggle"
      >
        {sidebarOpen ? <PanelRightClose size={20} /> : <PanelRightOpen size={20} />}
      </button>
    </header>
  );
}

// ─── Sağ Panel: Kurs İçeriği ──────────────────────────────────────────────────

function CabinSidebar({
  lessons,
  completedLessonIds,
  currentLessonId,
  courseId,
  navigate,
  getField,
  lang,
  t,
  sidebarRef,
  currentItemRef,
  course,
}) {
  const completedInCourse = completedLessonIds.filter(id =>
    lessons.some(l => l.lesson_id === id)
  ).length;

  // Modül 3.7.3 / CPD blokaj çözümü: final sınavı ders akışı içinde de görünür
  // olmalı (CourseDetail'daki AssessmentCard ile aynı davranış). Kabin Memurluğu
  // kursu hariç — o kursun kendi sınav akışı var (bkz. CABIN_COURSE_ID).
  const examEnabled = !!course?.final_exam?.enabled && courseId !== CABIN_COURSE_ID;
  // Kilit durumu paylaşılan formülle hesaplanır (bkz. utils/assessment.js) —
  // CourseDetail'daki AssessmentCard ile aynı kaynağı kullanır, ıraksama olmaz.
  // Bu bileşende backend `enrollment.progress` mevcut olmadığından client-side
  // sayıma düşülür (aynı fonksiyonun ikinci dalı).
  const assessmentUnlocked = isAssessmentUnlocked({ lessons, completedLessonIds });

  return (
    <aside
      className="w-full lg:w-80 xl:w-96 lg:shrink-0 bg-[var(--cabin-panel-bg)] border-t border-[var(--cabin-divider)] lg:border-t-0 lg:border-l lg:border-[var(--cabin-divider)] flex flex-col lg:sticky lg:top-0 lg:max-h-screen"
      data-testid="cabin-udemy-sidebar"
      aria-label={t('cabin.lesson.course_content')}
    >
      {/* Panel başlığı */}
      <div className="px-4 py-3 border-b border-[var(--cabin-divider)] bg-[var(--cabin-bar-bg)]">
        <h2 className="text-white font-bold text-sm">
          {t('cabin.lesson.course_content')}
        </h2>
        <p className="text-[#7BAABF] text-xs mt-0.5">
          {t('cabin.lesson.progress_label')
            .replace('{done}', completedInCourse)
            .replace('{total}', lessons.length)}
        </p>
      </div>

      {/* Ders listesi */}
      <div
        ref={sidebarRef}
        className="flex-1 min-h-0 overflow-y-auto"
      >
        {lessons.map((l) => {
          const isCompleted = completedLessonIds.includes(l.lesson_id);
          const isCurrent = l.lesson_id === currentLessonId;
          const duration = formatDuration(l.duration_minutes, lang);

          return (
            <button
              key={l.lesson_id}
              ref={isCurrent ? currentItemRef : null}
              onClick={() => navigate(`/lesson/${courseId}/${l.lesson_id}`)}
              className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-[var(--cabin-divider)] transition-colors ${
                isCurrent
                  ? 'bg-[#1E4060] border-l-4 border-l-[var(--brand-primary)]'
                  : isCompleted
                  ? 'bg-[#0B2D43] hover:bg-[#1E4060] opacity-90'
                  : 'bg-[var(--cabin-panel-bg)] hover:bg-[#1E4060]'
              }`}
              data-testid={`cabin-sidebar-lesson-${l.lesson_id}`}
            >
              {/* Tamamlama göstergesi */}
              <div
                className={`mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                  isCompleted
                    ? 'bg-[var(--brand-primary)]'
                    : isCurrent
                    ? 'ring-2 ring-[var(--brand-primary)] bg-[#1A3450]'
                    : 'bg-[#2A5070]'
                }`}
              >
                {isCompleted ? (
                  <Check size={11} strokeWidth={3} className="text-white" />
                ) : (
                  <span className="text-[9px] font-bold text-[#7BAABF]">
                    {l.order || ''}
                  </span>
                )}
              </div>

              {/* Başlık + süre */}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-xs font-semibold leading-snug ${
                    isCurrent ? 'text-white' : isCompleted ? 'text-[#7BAABF]' : 'text-[#D0E8F5]'
                  }`}
                >
                  {getField(l, 'title')}
                </p>
                {duration && (
                  <p className="text-[10px] text-[#7BAABF] mt-0.5">{duration}</p>
                )}
              </div>
            </button>
          );
        })}

        {/* Değerlendirme / Assessment — müfredat sekmesindeki AssessmentCard ile aynı
            davranış ve i18n key'leri: final_exam.enabled=true ise her zaman görünür,
            %100 tamamlanana kadar kilitli. */}
        {examEnabled && (
          <button
            type="button"
            onClick={assessmentUnlocked ? () => navigate(`/exam/${courseId}`) : undefined}
            aria-disabled={!assessmentUnlocked}
            disabled={!assessmentUnlocked}
            className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-[var(--cabin-divider)] transition-colors ${
              assessmentUnlocked
                ? 'bg-[var(--cabin-panel-bg)] hover:bg-[#1E4060] cursor-pointer'
                : 'bg-[var(--cabin-panel-bg)] opacity-70 cursor-not-allowed'
            }`}
            data-testid="lesson-nav-assessment-item"
          >
            <div
              className={`mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                assessmentUnlocked ? 'bg-[var(--brand-primary)]' : 'bg-[#2A5070]'
              }`}
            >
              {assessmentUnlocked ? (
                <FileCheck size={11} strokeWidth={2.5} className="text-white" />
              ) : (
                <Lock size={10} strokeWidth={2.5} className="text-[#7BAABF]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-xs font-semibold leading-snug ${
                  assessmentUnlocked ? 'text-white' : 'text-[#D0E8F5]'
                }`}
              >
                {t('courseDetail.curriculum.assessment.title')}
              </p>
              <p className="text-[10px] text-[#7BAABF] mt-0.5">
                {assessmentUnlocked
                  ? t('courseDetail.curriculum.assessment.unlockedSub')
                  : t('courseDetail.curriculum.assessment.lockedSub')}
              </p>
            </div>
          </button>
        )}
      </div>
    </aside>
  );
}

// ─── Notlar sekmesi ───────────────────────────────────────────────────────────

function NotesTab({ courseId, lessonId, lang, t }) {
  const toast = useToast();
  const { notes, loading, error, addNote, deleteNote, adding, noteInput, setNoteInput } =
    useNotes(courseId, lessonId);

  const handleAdd = useCallback(async () => {
    if (!noteInput.trim()) return;
    try {
      await addNote(noteInput);
    } catch {
      toast.error(lang === 'tr' ? 'Not eklenemedi.' : 'Could not add note.');
    }
  }, [noteInput, addNote, toast, lang]);

  const handleDelete = useCallback(async (noteId) => {
    try {
      await deleteNote(noteId);
    } catch {
      toast.error(lang === 'tr' ? 'Not silinemedi.' : 'Could not delete note.');
    }
  }, [deleteNote, toast, lang]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleAdd();
    }
  };

  return (
    <div className="p-4 max-w-2xl" data-testid="cabin-notes-tab">
      <h3 className="font-bold text-base mb-3 text-[var(--text-main)]">
        {t('cabin.lesson.notes_title')}
      </h3>

      {/* Girdi alanı */}
      <div className="flex flex-col gap-2 mb-4">
        <textarea
          value={noteInput}
          onChange={e => setNoteInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('cabin.lesson.notes_placeholder')}
          rows={3}
          className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-medium text-[var(--text-main)] resize-none focus:outline-none focus:border-cyan-400 transition"
          data-testid="notes-textarea"
        />
        <button
          onClick={handleAdd}
          disabled={adding || !noteInput.trim()}
          className="self-end btn-primary px-5 py-2 text-sm disabled:opacity-50"
          data-testid="notes-save-btn"
        >
          {adding ? '...' : t('cabin.lesson.notes_save')}
        </button>
      </div>

      {/* Liste */}
      {loading && (
        <p className="text-sm text-[var(--text-muted)] font-medium" data-testid="notes-loading">
          {t('cabin.lesson.notes_loading')}
        </p>
      )}
      {error && !loading && (
        <p className="text-sm text-rose-500 font-medium" data-testid="notes-error">
          {t('cabin.lesson.notes_error')}
        </p>
      )}
      {!loading && !error && notes.length === 0 && (
        <p className="text-sm text-[var(--text-muted)] italic" data-testid="notes-empty">
          {t('cabin.lesson.notes_empty')}
        </p>
      )}
      {!loading && notes.length > 0 && (
        <ul className="space-y-3" data-testid="notes-list">
          {notes.map((note) => {
            const id = note.note_id || note._id || note.id;
            const createdAt = note.created_at
              ? new Date(note.created_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : '';
            return (
              <li
                key={id}
                className="bg-white rounded-xl border-2 border-[var(--border-light)] p-3 flex items-start gap-3"
                data-testid={`note-item-${id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-main)] whitespace-pre-wrap break-words">
                    {note.content}
                  </p>
                  {createdAt && (
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">{createdAt}</p>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(id)}
                  className="shrink-0 text-rose-400 hover:text-rose-600 transition"
                  aria-label={t('cabin.lesson.notes_delete')}
                  data-testid={`note-delete-${id}`}
                >
                  <Trash2 size={15} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Genel Bakış sekmesi ──────────────────────────────────────────────────────

function OverviewTab({ lesson, course, getField, lang: _lang, t }) {
  const description = getField(lesson, 'description');
  // Sayi bilinmiyorsa (ikisi de yok) satir gizli kalir; varsa ogrenci yuzu sayisi.
  const hasCount = (course?.display_student_count ?? course?.enrolled_count) != null;
  const enrolledCount = hasCount ? getStudentCount(course) : null;

  return (
    <div className="p-4 max-w-2xl" data-testid="cabin-overview-tab">
      {/* Kurs başlığı + meta */}
      <h2 className="font-bold text-lg text-[var(--text-main)] mb-1">
        {getField(course, 'title') || ''}
      </h2>
      {enrolledCount !== null && (
        <p className="text-sm text-[var(--text-muted)] font-medium mb-3">
          {t('cabin.lesson.students').replace('{n}', enrolledCount)}
        </p>
      )}

      {/* Ders açıklaması */}
      <h3 className="font-bold text-sm mb-2 text-[var(--text-main)]">
        {t('cabin.lesson.description_title')}
      </h3>
      {description ? (
        <p className="text-sm font-medium text-[var(--text-muted)] leading-relaxed whitespace-pre-wrap">
          {description}
        </p>
      ) : (
        <p className="text-sm font-medium text-[var(--text-muted)] italic" data-testid="cabin-overview-empty">
          {t('cabin.lesson.description_empty')}
        </p>
      )}
    </div>
  );
}

// ─── Ana bileşen ──────────────────────────────────────────────────────────────

/**
 * CabinLessonLayout — Udemy benzeri tam sayfa ders oynatma düzeni.
 * Tüm kurslar için generic olarak kullanılır (artık course_cabin_main_v1'e özgü değil).
 *
 * Props:
 *   lesson              — mevcut ders objesi
 *   courseId            — kurs ID'si (herhangi bir kurs)
 *   lessons             — tüm dersler listesi
 *   completedLessonIds  — tamamlanan ders ID'leri
 *   course              — kurs meta objesi (title + enrolled_count için)
 *   prevLesson          — önceki ders (null olabilir)
 *   nextLesson          — sonraki ders (null olabilir)
 *   isCompleted         — mevcut ders tamamlandı mı
 *   completing          — tamamlama işlemi devam ediyor mu
 *   onManualComplete    — "Dersi Tamamla" butonu callback'i
 *   children            — LessonDispatcher (video player)
 */
export default function CabinLessonLayout({
  lesson,
  courseId,
  lessons = [],
  completedLessonIds = [],
  course = null,
  prevLesson,
  nextLesson,
  isCompleted,
  completing,
  onManualComplete,
  children,
}) {
  const navigate = useNavigate();
  const { getField, lang, t } = useLanguage();
  const lessonId = lesson?.lesson_id;

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sidebarRef = useRef(null);
  const currentItemRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  // Aktif ders değişince sidebar'da ortala
  useEffect(() => {
    const container = sidebarRef.current;
    const item = currentItemRef.current;
    if (!container || !item) return;
    const targetScrollTop =
      item.offsetTop - container.clientHeight / 2 + item.offsetHeight / 2;
    if (isFirstRenderRef.current) {
      container.scrollTop = targetScrollTop;
      isFirstRenderRef.current = false;
    } else {
      container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    }
  }, [lessonId]);

  const completedCount = completedLessonIds.filter(id =>
    lessons.some(l => l.lesson_id === id)
  ).length;

  const totalCount = lessons.length;

  return (
    <div className="flex flex-col min-h-screen bg-[var(--cabin-video-bg)]" data-testid="cabin-udemy-layout">
      {/* Üst bar */}
      <CabinTopBar
        lesson={lesson}
        course={course}
        getField={getField}
        lang={lang}
        t={t}
        completedCount={completedCount}
        totalCount={totalCount}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(v => !v)}
        courseId={courseId}
        navigate={navigate}
      />

      {/* Gövde: video alanı + (opsiyonel) sağ panel
          Masaüstünde yatay (flex-row), mobilde dikey (flex-col). Doğal sayfa akışı. */}
      <div className="flex flex-col lg:flex-row" data-testid="cabin-udemy-body">

        {/* Sol / Ana alan: video + alt sekmeler + prev/next — doğal akış */}
        <div
          className="flex flex-col flex-1 min-w-0"
          data-testid="cabin-main-area"
        >
          {/* Video alanı — masaüstünde viewport yüksekliğine letterbox ile sığdır, kırpma yok.
              Global Navbar gizli; 100vh tamamı bu sayfaya ait.
              Üst bar ~3.5rem + prev/next bar ~3rem = 6.5rem; video kutusu bu yüksekliği aşmaz.
              overflow-hidden KULLANILMIYOR: taşma olmadığı için gereksiz + kontrol çubuğunu kesmez.
              video kutusu max-h ile sınırlı, aspect-video + w-auto ile genişlik aspect'ten türer
              (letterbox: yanlarda boşluk kalır, alttan kesilmez). */}
          <div
            className="w-full shrink-0 bg-[var(--cabin-video-bg)] flex items-center justify-center lg:h-[calc(100vh-6.5rem)]"
            data-testid="cabin-video-wrapper"
          >
            <div
              className="w-full lg:h-full lg:flex lg:items-center lg:justify-center lg:py-0"
              data-testid="cabin-video-inner"
            >
              {children}
            </div>
          </div>

          {/* materials_required uyarısı — her zaman görünür, sekmelerden bağımsız */}
          {lesson?.materials_required && (
            <div
              className="mx-4 mt-3 mb-0 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-lg text-xs font-bold"
              data-testid="required-material-notice"
            >
              <AlertCircle size={14} />
              {lang === 'tr'
                ? 'Bu ders zorunludur — tamamlamadan sınava giremezsiniz'
                : 'This lesson is required — cannot take exam without completing'}
            </div>
          )}

          {/* Önceki / Sonraki + Tamamla — video altı */}
          <div className="bg-[var(--cabin-bar-bg)] border-b border-[var(--cabin-divider)] px-4 py-2 flex items-center justify-between gap-3 flex-wrap shrink-0">
            {/* Önceki */}
            <div className="shrink-0">
              {prevLesson ? (
                <button
                  onClick={() => navigate(`/lesson/${courseId}/${prevLesson.lesson_id}`)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#2A5070] text-[#A8C8DC] hover:bg-[#1E4060] text-xs font-semibold transition"
                  data-testid="cabin-prev-btn"
                >
                  <ChevronLeft size={14} />
                  {lang === 'tr' ? 'Önceki' : 'Previous'}
                </button>
              ) : (
                <div />
              )}
            </div>

            {/* Tamamla + Sonraki */}
            <div className="flex items-center gap-2 shrink-0">
              {!isCompleted ? (
                <button
                  onClick={onManualComplete}
                  disabled={completing}
                  className="px-4 py-1.5 rounded-lg bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50 transition"
                  data-testid="cabin-complete-btn"
                >
                  <Check size={14} strokeWidth={3} />
                  {completing ? '...' : (lang === 'tr' ? 'Dersi Tamamla' : 'Complete Lesson')}
                </button>
              ) : (
                <div
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0E3A52] text-[#7BAABF] text-xs font-bold"
                  data-testid="cabin-completed-badge"
                >
                  <Check size={13} strokeWidth={3} />
                  {lang === 'tr' ? 'Tamamlandı' : 'Completed'}
                </div>
              )}

              {nextLesson ? (
                <button
                  onClick={() => navigate(`/lesson/${courseId}/${nextLesson.lesson_id}`)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1E4060] hover:bg-[#2A5070] text-white text-xs font-semibold transition"
                  data-testid="cabin-next-btn"
                >
                  {lang === 'tr' ? 'Sonraki' : 'Next'}
                  <ChevronRight size={14} />
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/courses/${courseId}`)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white text-xs font-bold transition"
                  data-testid="cabin-finish-btn"
                >
                  {lang === 'tr' ? 'Kursu Bitir' : 'Finish Course'}
                  <Check size={13} />
                </button>
              )}
            </div>
          </div>

          {/* Sekmeler: Genel Bakış | Notlar — doğal akış, video altında görünür */}
          <div className="bg-white">
            <Tabs defaultValue="overview" className="h-full">
              <TabsList
                className="w-full justify-start rounded-none border-b-2 border-[var(--border-light)] bg-white px-4 h-11 sticky top-0 z-10"
                data-testid="cabin-tabs-list"
              >
                <TabsTrigger
                  value="overview"
                  className="text-sm font-semibold data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] data-[state=active]:text-[var(--brand-primary)] rounded-none pb-2.5 px-3"
                  data-testid="cabin-tab-overview"
                >
                  {t('cabin.lesson.tab_overview')}
                </TabsTrigger>
                <TabsTrigger
                  value="notes"
                  className="text-sm font-semibold data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-primary)] data-[state=active]:text-[var(--brand-primary)] rounded-none pb-2.5 px-3"
                  data-testid="cabin-tab-notes"
                >
                  {t('cabin.lesson.tab_notes')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <OverviewTab
                  lesson={lesson}
                  course={course}
                  getField={getField}
                  lang={lang}
                  t={t}
                />
              </TabsContent>

              <TabsContent value="notes">
                <NotesTab
                  courseId={courseId}
                  lessonId={lessonId}
                  lang={lang}
                  t={t}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Sağ panel: kurs içeriği — sidebarOpen ise görünür */}
        {sidebarOpen && (
          <CabinSidebar
            lessons={lessons}
            completedLessonIds={completedLessonIds}
            currentLessonId={lessonId}
            courseId={courseId}
            navigate={navigate}
            getField={getField}
            lang={lang}
            t={t}
            sidebarRef={sidebarRef}
            currentItemRef={currentItemRef}
            course={course}
          />
        )}
      </div>
    </div>
  );
}
